import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useBlocksByDistrict } from "../../../block/hooks/useBlock";
import { useAuth } from "../../../../modules/auth";
import { FileUpload } from "../../../registration/helper/FileUpload";
import { useSchemesMaster } from "../../../registration/hooks/useMasterData";
import { uploadSchemeReport } from "../../api/SchemeDataEntry.api";
import {
	useSchemeSubmit,
	useSchemeUpdate,
} from "../../hooks/useSchemeDataEntry";

type Props = {
	isView?: boolean;
	districts: any[];
	schemeId?: number;
	id?: string;
	initialData?: any;
};

// Which scheme this State/District Assembly meeting belongs to — mirrors
// HLC Entry Form's inner tab bar. NMP/CMBFS/NSAP/DOSJE share the built form
// below; Other is still a placeholder.
const TARGET_SCHEMES = ["NMP", "CMBFS", "NSAP", "DOSJE", "Other"];
const BUILT_TARGET_SCHEMES = ["NMP", "CMBFS", "NSAP", "DOSJE"];

const validationSchema = Yup.object({
	district_id: Yup.string().required("District is required"),
	block_id: Yup.string().required("Block is required"),
	assembly_date: Yup.string().required("Date is required"),
	location: Yup.string().required("Location is required"),
});

const initialValues = {
	brp_employee_code: "",
	brp_name: "",
	brp_mobile_no: "",
	district_id: "",
	block_id: "",
	assembly_date: "",
	location: "",
	meeting_minutes_file: null,
	photo_1: null,
	photo_2: null,
	photo_3: null,
};

const BlockDistrictAssemblyForm: React.FC<Props> = ({
	isView = false,
	districts,
	schemeId,
	id,
	initialData,
}) => {
	const navigate = useNavigate();
	const { currentUser } = useAuth() as any;
	const submitMutation = useSchemeSubmit();
	const updateMutation = useSchemeUpdate();
	const { data: schemesMaster = [] } = useSchemesMaster();
	const [targetScheme, setTargetScheme] = useState(
		initialData?.target_scheme_name || "NMP",
	);

	// Real scheme_id (from the schemes master table) for whichever scheme
	// was picked via the inner tab bar — saved into json_data alongside
	// target_scheme_name so reports can join back to the schemes table.
	const targetSchemeId = schemesMaster.find(
		(s: any) => s.name === targetScheme,
	)?.id;

	const mergedInitialValues = {
		...initialValues,
		brp_employee_code:
			initialData?.brp_employee_code || currentUser?.employee_code || "",
		brp_name: initialData?.brp_name || currentUser?.name || "",
		brp_mobile_no:
			initialData?.brp_mobile_no || currentUser?.mobile || "",
		...(initialData || {}),
	};

	const onSubmit = async (values: any, actions: any) => {
		try {
			const dataToSave = {
				...values,
				scheme_id: schemeId,
				target_scheme_name: targetScheme,
				target_scheme_id: targetSchemeId,
			};
			if (id) {
				await updateMutation.mutateAsync({ id, data: dataToSave });
				toast.success("State/District Assembly report updated successfully");
			} else {
				await submitMutation.mutateAsync(dataToSave);
				toast.success("State/District Assembly report submitted successfully");
			}
			navigate("/scheme-data-entry");
		} catch (error: any) {
			const errorMessage =
				error.response?.data?.message ||
				error.response?.data?.error ||
				error.message ||
				"Failed to submit report";
			toast.error(errorMessage);
		} finally {
			actions.setSubmitting(false);
		}
	};

	return (
		<div className="card shadow-sm mb-5">
			<div className="card-header border-0 pt-5">
				<h3 className="card-title align-items-start flex-column">
					<span className="card-label fw-bold fs-3 mb-1">
						State/District Assembly Data Entry
					</span>
				</h3>
			</div>
			<div className="card-body py-3">
				{/* Which scheme this State/District Assembly meeting belongs to —
				    mirrors HLC Entry Form's inner tab bar. */}
				<div className="d-flex flex-wrap border rounded mb-8 overflow-hidden">
					{TARGET_SCHEMES.map((name) => {
						const isActive = targetScheme === name;
						return (
							<button
								key={name}
								type="button"
								className={`btn rounded-0 flex-grow-1 fw-bold ${
									isActive
										? "btn-light"
										: "btn-secondary bg-light-secondary text-gray-600"
								}`}
								onClick={() => setTargetScheme(name)}
							>
								{name}
							</button>
						);
					})}
				</div>

				{!BUILT_TARGET_SCHEMES.includes(targetScheme) ? (
					<div className="text-center text-muted py-20 fw-semibold">
						The form for {targetScheme} is currently under construction. Please
						check back later.
					</div>
				) : (
					<Formik
						initialValues={mergedInitialValues}
						validationSchema={validationSchema}
						onSubmit={onSubmit}
						enableReinitialize={true}
					>
						{(formik) => (
							<BlockDistrictAssemblyFormBody
								formik={formik}
								isView={isView}
								districts={districts}
							/>
						)}
					</Formik>
				)}
			</div>
		</div>
	);
};

const BlockDistrictAssemblyFormBody: React.FC<{
	formik: any;
	isView: boolean;
	districts: any[];
}> = ({ formik, isView, districts }) => {
	const { values, setFieldValue, isSubmitting } = formik;
	const navigate = useNavigate();
	const { data: blocksData } = useBlocksByDistrict(values.district_id || 0);
	const blocks = blocksData || [];

	// Clear the selected block whenever the district changes so a stale
	// block from a different district can never be submitted.
	useEffect(() => {
		if (!values.district_id) {
			setFieldValue("block_id", "");
		}
	}, [values.district_id, setFieldValue]);

	return (
		<Form className="form">
			<div className="row g-9 mb-8">
				<div className="col-md-4 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Employee Code
					</label>
					<Field
						name="brp_employee_code"
						className="form-control form-control-solid"
						disabled={true}
						readOnly={true}
					/>
				</div>

				<div className="col-md-4 fv-row">
					<label className="form-label fs-5 fw-bold text-dark">
						Name
					</label>
					<Field
						name="brp_name"
						className="form-control form-control-solid"
						disabled={true}
						readOnly={true}
					/>
				</div>

				<div className="col-md-4 fv-row">
					<label className="form-label fs-5 fw-bold text-dark">
						Mobile No
					</label>
					<Field
						name="brp_mobile_no"
						className="form-control form-control-solid"
						disabled={true}
						readOnly={true}
					/>
				</div>
			</div>
			<div className="row g-9 mb-8">
				<div className="col-md-3 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						District
					</label>
					<Field
						as="select"
						name="district_id"
						className="form-select form-select-solid"
						disabled={isView}
						onChange={(e: any) => {
							setFieldValue("district_id", e.target.value);
							setFieldValue("block_id", "");
						}}
					>
						<option value="">Select District</option>
						{districts?.map((d: any) => (
							<option key={d.id} value={d.id}>
								{d.name}
							</option>
						))}
					</Field>
					<div className="text-danger mt-1 fw-bold fs-7">
						<ErrorMessage name="district_id" />
					</div>
				</div>

				<div className="col-md-3 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Block
					</label>
					<Field
						as="select"
						name="block_id"
						className="form-select form-select-solid"
						disabled={isView || !values.district_id}
					>
						<option value="">Select Block</option>
						{blocks?.map((b: any) => (
							<option key={b.id} value={b.id}>
								{b.name}
							</option>
						))}
					</Field>
					<div className="text-danger mt-1 fw-bold fs-7">
						<ErrorMessage name="block_id" />
					</div>
				</div>

				<div className="col-md-3 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Date
					</label>
					<Field
						name="assembly_date"
						type="date"
						className="form-control form-control-solid"
						disabled={isView}
					/>
					<div className="text-danger mt-1 fw-bold fs-7">
						<ErrorMessage name="assembly_date" />
					</div>
				</div>

				<div className="col-md-3 fv-row">
					<label className="form-label required fs-5 fw-bold text-dark">
						Location
					</label>
					<Field
						name="location"
						className="form-control form-control-solid"
						disabled={isView}
					/>
					<div className="text-danger mt-1 fw-bold fs-7">
						<ErrorMessage name="location" />
					</div>
				</div>
			</div>

			<div className="row g-9 mb-8">
				<div className="col-md-6 fv-row">
					<FileUpload
						label="Meeting Minutes (PDF)"
						value={values.meeting_minutes_file}
						disabled={isView}
						onChange={(val) => setFieldValue("meeting_minutes_file", val)}
						onUpload={async (file) => {
							if (
								file.type !== "application/pdf" &&
								!file.name.toLowerCase().endsWith(".pdf")
							) {
								toast.error("Only PDF format files are allowed!");
								throw new Error("Only PDF format files are allowed!");
							}
							return await uploadSchemeReport(
								file,
								"block-district-assembly-minutes",
							);
						}}
						accept="application/pdf,.pdf"
						maxSizeMB={5}
						description="Max 5MB (PDF format only)"
					/>
				</div>
			</div>

			<div className="row g-9 mb-8">
				<div className="col-md-4 fv-row">
					<FileUpload
						label="Photo 1"
						value={values.photo_1}
						disabled={isView}
						onChange={(val) => setFieldValue("photo_1", val)}
						onUpload={(file) =>
							uploadSchemeReport(file, "block-district-assembly-photo")
						}
						accept="image/*"
						maxSizeMB={2}
						description="Max 2MB (JPG, PNG)"
					/>
				</div>
				<div className="col-md-4 fv-row">
					<FileUpload
						label="Photo 2"
						value={values.photo_2}
						disabled={isView}
						onChange={(val) => setFieldValue("photo_2", val)}
						onUpload={(file) =>
							uploadSchemeReport(file, "block-district-assembly-photo")
						}
						accept="image/*"
						maxSizeMB={2}
						description="Max 2MB (JPG, PNG)"
					/>
				</div>
				<div className="col-md-4 fv-row">
					<FileUpload
						label="Photo 3"
						value={values.photo_3}
						disabled={isView}
						onChange={(val) => setFieldValue("photo_3", val)}
						onUpload={(file) =>
							uploadSchemeReport(file, "block-district-assembly-photo")
						}
						accept="image/*"
						maxSizeMB={2}
						description="Max 2MB (JPG, PNG)"
					/>
				</div>
			</div>

			<div className="d-flex justify-content-end mb-5">
				<button
					type="button"
					className="btn btn-light me-3"
					onClick={() => navigate("/scheme-data-entry")}
				>
					Cancel
				</button>
				{!isView && (
					<button
						type="submit"
						className="btn btn-primary"
						disabled={isSubmitting}
					>
						{isSubmitting ? "Submitting..." : "Submit"}
					</button>
				)}
			</div>
		</Form>
	);
};

export default BlockDistrictAssemblyForm;
